import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Typography, Button, Flex } from "antd";
import {
  disableOnWheel,
  sanitizeDataTypeClientSide,
} from "../../../../../utils/utils";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import { colors } from "../../../../../constants/colors";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import {
  getUserProfileAction,
  updateBankDetailsAction,
} from "../../../../../redux/services/auth/authServices";
import { useEffect } from "react";
import { appConfig } from "../../../../../constants/config/appConfig";
import styles from "./AddBankAccount.module.scss";
import { allowCharactersOnlyForDecimalNumbers } from "../../../../../utils/appKeyStrokeLimitingUtils";
const { Title } = Typography;

const UpdateBankAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /* local storage dependencies */
  const { setUserSession } = useLocalStorage(appConfig.SESSION_USER_INFO);
  /* local storage dependencies */

  useEffect(() => {
    dispatch(getUserProfileAction(setUserSession)).then((apiRes) => {
      const userDetails = apiRes;
      if (
        userDetails &&
        userDetails?.organization_id &&
        userDetails?.organization_id !== null &&
        userDetails?.organization_id !== undefined &&
        userDetails?.organization_id?.payout_details &&
        userDetails?.organization_id?.payout_details !== null &&
        userDetails?.organization_id?.payout_details !== undefined
      ) {
        const tempBankDetails = userDetails?.organization_id?.payout_details;
        form.setFieldsValue({
          beneficiaryName: tempBankDetails?.account_holder_name,
          accountNumber: tempBankDetails?.account_number,
          confirmAccountNumber: tempBankDetails?.account_number,
          bankCode: tempBankDetails?.bank_code,
          swiftCode: tempBankDetails?.swift_code,
          bankName: tempBankDetails?.bank_name,
          routing_number: tempBankDetails?.routing_number,
        });
      }
    });
  }, []);

  const onSubmit = (values) => {
    let dataToBeSent = {
      payout_details: {
        account_holder_name: values?.beneficiaryName,
        account_number: values?.confirmAccountNumber,
        bank_code: values?.bankCode,
        bank_name: values?.bankName,
        swift_code: values?.swiftCode,
        routing_number: values?.routing_number
      },
      payment_type:"bank"
    };
    dispatch(
      updateBankDetailsAction(
        dataToBeSent,
        navigate,
        setLoading,
        setUserSession
      )
    );
  };

  return (
    <>
      <Flex vertical gap="1rem" className={styles.addBankAccountContainer}>
        <Title level={1} style={{ color: colors.primaryColor }}>
          {t("addBankAccount.editABankAccount")}
        </Title>
        <Title level={4} type="secondary" style={{ margin: 0 }}>
          {t("addBankAccount.subTitle")}
        </Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
          requiredMark={false}
        >
          <Flex gap="1rem" vertical>
          <div>
                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.beneficiaryNameLabel")}
                    required={true}
                    />
                }
                name="beneficiaryName"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.beneficiaryNameErrorMessage"),
                    },
                    {
                    whitespace: true,
                    message: t("addBankAccount.beneficiaryNameErrorMessage"),
                    },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t(
                    "addBankAccount.beneficiaryNameLabelPlaceholder"
                    )}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.accountNumberLabel")}
                    required={true}
                    />
                }
                name="accountNumber"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.accountNumberLabelErrorMessage"),
                    },
                    {
                      whitespace: true,
                      message: t("addBankAccount.confirmAccountNumberErrorMessage"),
                    }
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                  <Input
                    placeholder={t("addBankAccount.accountNumberLabelPlaceholder")}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.confirmAccountNumberLabel")}
                    required={true}
                    />
                }
                name="confirmAccountNumber"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.confirmAccountNumberErrorMessage"),
                    },
                    {
                      whitespace: true,
                      message: t("addBankAccount.confirmAccountNumberErrorMessage"),
                    }
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t(
                      "addBankAccount.confirmAccountNumberPlaceholder"
                      )}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.ifcsCodeLabel")}
                    required={true}
                    />
                }
                name="bankCode"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.ifcsCodeErrorMessage"),
                    },
                    {
                    whitespace: true,
                    message: t("addBankAccount.ifcsCodeErrorMessage"),
                    }
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t("addBankAccount.ifcsCodePlaceholder")}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.bankNameLabel")}
                    required={true}
                    />
                }
                name="bankName"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.bankNameValidErrorMessage"),
                    },
                    {
                    whitespace: true,
                    message: t("addBankAccount.bankNameValidErrorMessage"),
                    },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t(
                    "addBankAccount.bankNamePlaceholder"
                    )}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.swiftCodeLabel")}
                    required={true}
                    />
                }
                name="swiftCode"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.swiftCodeErrorMessage"),
                    },
                    {
                    whitespace: true,
                    message: t("addBankAccount.swiftCodeErrorMessage"),
                    }
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t("addBankAccount.swiftCodePlaceholder")}
                />
                </Form.Item>

                <Form.Item
                label={
                    <RequiredLabel
                    text={t("addBankAccount.routingNumber")}
                    required={true}
                    />
                }
                name="routing_number"
                rules={[
                    {
                    required: true,
                    message: t("addBankAccount.pleaseEnterRoutingNumber"),
                    },
                    {
                    whitespace: true,
                    message: t("addBankAccount.pleaseEnterRoutingNumber")
                    }
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                <Input
                    placeholder={t("addBankAccount.enterRoutingNumber")}
                />
                </Form.Item>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: "100%" }}
            >
              {t("addBankAccount.update")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </>
  );
};

export default UpdateBankAccount;
